import axios from 'axios';
import getCookie from '@helpers/getCookie';
import DataLayer from '../Global/DataLayer';

const getItems = (force = false) => {
  return new Promise((resolve, reject) => {
    if (!getCookie('cart')) {
      if (sessionStorage.getItem('Cart')) {
        sessionStorage.removeItem('Cart');
      }

      const eventCartUpdate = new CustomEvent('cart:update', { detail: null });
      window.dispatchEvent(eventCartUpdate);

      return resolve(null);
    }

    if (!force && !!sessionStorage.getItem('Cart')) {
      return resolve(JSON.parse(sessionStorage.getItem('Cart')));
    }

    axios
      .get('/rest/cart', {
        params: {
          paymentMethodId: getCookie('paymentMethodId')
        }
      })
      .then((res) => {
        if (res.data) {
          sessionStorage.setItem('Cart', JSON.stringify(res.data));
        } else {
          if (sessionStorage.getItem('Cart')) {
            sessionStorage.removeItem('Cart');
          }
        }

        const eventCartUpdate = new CustomEvent('cart:update', {
          detail: res.data
        });

        window.dispatchEvent(eventCartUpdate);

        return resolve(res.data);
      })
      .catch((err) => {
        if (sessionStorage.getItem('Cart')) {
          sessionStorage.removeItem('Cart');
        }

        const eventUserReload = new CustomEvent('cart:update', { detail: null });
        window.dispatchEvent(eventUserReload);
        return reject(
          err && err.response && err.response.data ? err.response.data : null
        );
      });
  });
};

const add = (id, qty, isUpdateCart = true, trackingInfo) => {
  let items = [];
  if (Array.isArray(id)) {
    items = id;
  } else {
    items.push({
      id: id,
      qty: qty
    });
  }

  return new Promise((resolve, reject) => {
    axios
      .post('/rest/cart/add', {
        items: items
      })
      .then((res) => {
        if (isUpdateCart) {
          updateCart();
        }

        if (res && res.data && !res.data.success) {
          reject(res.data);
          return;
        }

        dataLayerFuncAdd(trackingInfo, id);

        return resolve(res.data);
      })
      .catch((err) => {
        return reject(
          err && err.response && err.response.data ? err.response.data : null
        );
      });
  });
};

const update = (id, qty, isUpdateCart = true, trackingInfo) => {
  return new Promise((resolve, reject) => {
    axios
      .put('/rest/cart/update', {
        id: id,
        units: qty
      })
      .then((res) => {
        if (isUpdateCart) {
          updateCart();
        }

        return resolve(res.data);
      })
      .catch(() => {
        return reject(new Error());
      });
  });
};

const updateCart = function () {
  return getItems(true);
};

const deleteItem = (id, isUpdateCart = true, trackingInfo) => {
  return new Promise((resolve, reject) => {
    axios
      .delete(`/rest/cart/${id}`)
      .then((res) => {
        if (isUpdateCart) {
          updateCart();
          const tracking = trackingInfo.map((item) => {
            return {
              id: item.shoppingCartItemID,
              name: item && item.productName.substring(3, 10),
              price: item && item.unitPriceText,
              category: item && item.lotteryName,
              variant:
                (item && item.productName.slice(item.productName.length - 3)) ||
                0,
              quantity: (item && item.unitsAcquired) || 0
            };
          });

          const trackRemove = tracking.map((key, index) => {
            return key.id === id ? tracking[index] : undefined;
          });

          dataLayerFuncRemove(trackRemove[0], id);
        }
        return resolve(res.data);
      })
      .catch((err) => {
        console.error(err);
        return reject(new Error());
      });
  });
};

const emptyCart = () => {
  return new Promise((resolve, reject) => {
    // Pega os items que serão excluídos para o trackeamento
    return getItems()
      .then((cart) => {
        const products = cart.items.map((item) => {
          return {
            id: item.shoppingCartItemID,
            name: item && item.productName.substring(3, 10),
            price: item && item.unitPriceText,
            category: item && item.lotteryName,
            variant:
              (item && item.productName.slice(item.productName.length - 3)) ||
              0,
            quantity: (item && item.unitsAcquired) || 0
          };
        });

        dataLayerFuncClearCart(products);
      })
      .finally(() => {
        axios
          .delete('/rest/cart/empty')
          .then((res) => {
            updateCart();
            return resolve(res.data);
          })
          .catch(() => {
            return reject(new Error());
          });
      });
  });
};

const redoGame = (orderID, productID = 0) => {
  return new Promise((resolve, reject) => {
    axios
      .post('/rest/cart/redogame', {
        orderID,
        productID
      })
      .then((res) => {
        updateCart();
        return resolve(res.data);
      })
      .catch(() => {
        return reject(new Error());
      });
  });
};

const currentPaymentMethod = () => {
  return new Promise((resolve, reject) => {
    axios
      .get('/rest/checkout/current-payment-method')
      .then((res) => {
        updateCart();
        return resolve(res.data);
      })
      .catch(() => {
        return reject(new Error());
      });
  });
};

const redoRecurrence = (recurringPaymentID) => {
  return new Promise((resolve, reject) => {
    axios
      .post('/rest/cart/redo-recurrence', {
        recurringPaymentID: recurringPaymentID
      })
      .then((res) => {
        updateCart();
        return resolve(res.data);
      })
      .catch(() => {
        return reject(new Error());
      });
  });
};

const dataLayerFuncAdd = (tracking, id) => {
  (Array.isArray(tracking) ? tracking : [tracking]).forEach((item) => {
    DataLayer.addToCart({
      name: (item && item.name) || '',
      id: id || undefined,
      price: (item && item.price) || 0,
      category: (item && item.category) || '',
      quantity: (item && item.unitsAcquired) || '',
      variant: (item && item.variant) || ''
    });
  });
};

const dataLayerFuncRemove = (tracking, id) => {
  (Array.isArray(tracking) ? tracking : [tracking]).forEach((item) => {
    DataLayer.removeFromCart({
      name: (item && item.name) || '',
      id: id || undefined,
      price: (item && item.price) || 0,
      category: (item && item.category) || '',
      quantity: (item && item.unitsAcquired) || '',
      variant: (item && item.variant) || ''
    });
  });
};

const dataLayerFuncClearCart = (products) => {
  DataLayer.clearCart(products);
};

const CartApi = {
  getItems: getItems,
  add: add,
  update: update,
  updateCart: updateCart,
  delete: deleteItem,
  empty: emptyCart,
  redoGame: redoGame,
  currentPaymentMethod: currentPaymentMethod,
  redoRecurrence: redoRecurrence,
  dataLayerFuncAdd: dataLayerFuncAdd,
  dataLayerFuncRemove: dataLayerFuncRemove
};

export default CartApi;
